import type { ReactNode } from 'react';
import {
  CAMPO_GASTO_LABELS,
  CAMPOS_GASTO,
  CATEGORIAS_PLANTILLA,
  EXPENSE_GROUP_KEYS,
  SHARED_LOCATION_ID,
  type CorreccionGasto,
  type DestinoColumna,
  type DudaGasto,
  type ExpenseGroupKey,
  type GastoLeido,
  type RevisionGastos,
  type TenantSettings,
} from '@alexiarest/shared';
import { formatDate, formatMonthLong, formatMoney, formatNumber } from '../format';
import { SmartSelect } from './Field';
import '../pages/ingredientes.css';
import '../pages/gastos.css';

/**
 * «Importar gastos · antes de guardar» (GastoExcelRevisar, rediseño de Alex,
 * 11 oct 2026). Tres cifras, «Así entendí tus columnas» plegado, «Revisa
 * estos N» con su arreglo al lado, «Lo que va a entrar» por grupo y los que
 * están bien plegados. Nada se guarda hasta «Guardar N gastos».
 */

const GRUPO: Record<ExpenseGroupKey, string> = {
  materia_prima: 'Materia prima',
  personal: 'Personal',
  generales: 'Gastos generales',
};

function opcionesCategoria(subgrupos?: TenantSettings['expenseSubgroups']) {
  const propias = EXPENSE_GROUP_KEYS.flatMap((g) => (subgrupos?.[g] ?? []).map((s) => `${GRUPO[g]} › ${s}`));
  const todas = [...CATEGORIAS_PLANTILLA.map((c) => c.label), ...propias.filter((p) => !CATEGORIAS_PLANTILLA.some((c) => c.label === p))];
  return [{ value: '', label: 'Elegir categoría…' }, ...todas.map((label) => ({ value: label, label }))];
}

export interface ColumnasVista {
  cabeceras: string[];
  columnas: DestinoColumna[];
  ignoradas: number[];
  onCambiar: (indice: number, destino: DestinoColumna) => void;
}

export function GastoExcelRevisar({
  revision,
  correcciones,
  corregir,
  locations,
  subgrupos,
  columnas,
  fechas,
}: {
  revision: RevisionGastos;
  correcciones: Record<number, CorreccionGasto>;
  corregir: (row: number, patch: CorreccionGasto) => void;
  locations: Array<{ locationId: string; name: string }>;
  subgrupos?: TenantSettings['expenseSubgroups'];
  /** Solo con un Excel propio (la plantilla no se empareja). */
  columnas?: ColumnasVista;
  /** Con fechas ambiguas: cómo se leyeron y el botón para darles la vuelta. */
  fechas?: { diaPrimero: boolean; onCambiar: () => void };
}): JSX.Element {
  const opciones = opcionesCategoria(subgrupos);
  const listos = revision.listos.length;
  const revisar = revision.porRevisar.length;
  const meses = new Set([...revision.listos, ...revision.porRevisar].map((i) => i.input.date.slice(0, 7)));

  const arreglo = (item: GastoLeido, duda: DudaGasto): ReactNode => {
    const row = item.row;
    switch (duda.kind) {
      case 'repetido':
        return (
          <div className="ing-revisar__arreglo">
            <button type="button" className="btn btn--sm" onClick={() => corregir(row, { repetido: 'misma' })}>Es la misma: no guardarla</button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => corregir(row, { repetido: 'otra' })}>Es otra</button>
          </div>
        );
      case 'categoria':
        return (
          <div className="ing-revisar__arreglo gasto-excel__categoria">
            <SmartSelect
              ariaLabel={`Categoría de la fila ${row}`}
              value={correcciones[row]?.categoria ?? ''}
              buscable
              placeholder="Elegir categoría…"
              onChange={(value) => value && corregir(row, { categoria: value })}
              options={opciones}
            />
            {item.supplierName && (
              <label className="gasto-excel__siempre">
                <input
                  type="checkbox"
                  checked={correcciones[row]?.siempre !== false}
                  onChange={(e) => corregir(row, { siempre: e.target.checked })}
                />
                Usar esta categoría siempre para {item.supplierName}
              </label>
            )}
          </div>
        );
      case 'fecha':
        return (
          <div className="ing-revisar__arreglo">
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => corregir(row, { fecha: 'ok' })}>Está bien</button>
          </div>
        );
      case 'iva':
        return (
          <div className="ing-revisar__arreglo">
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => corregir(row, { iva: 'ok' })}>Está bien</button>
          </div>
        );
      case 'devolucion':
        return (
          <div className="ing-revisar__arreglo">
            <button type="button" className="btn btn--sm" onClick={() => corregir(row, { devolucion: 'si' })}>Sí: no la guardo</button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => corregir(row, { devolucion: 'no' })}>No, es un gasto</button>
          </div>
        );
      case 'inversion':
        return (
          <div className="ing-revisar__arreglo">
            <button type="button" className="btn btn--sm" onClick={() => corregir(row, { inversion: 'si' })}>Mandarlo a Inversiones</button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => corregir(row, { inversion: 'no' })}>Es un gasto</button>
          </div>
        );
      case 'local':
        return (
          <div className="ing-revisar__arreglo" role="group" aria-label={`Local de la fila ${row}`}>
            {locations.map((l) => (
              <button key={l.locationId} type="button" className="chip" onClick={() => corregir(row, { locationId: l.locationId })}>{l.name}</button>
            ))}
            <button type="button" className="chip" onClick={() => corregir(row, { locationId: SHARED_LOCATION_ID })}>Todos (se reparte)</button>
          </div>
        );
    }
  };

  const usadas = columnas ? columnas.columnas.filter((c) => c !== 'ignorar').length : 0;
  const conNombre = columnas ? columnas.cabeceras.filter((h) => h.trim()).length : 0;
  const usa = (campo: DestinoColumna) => columnas?.columnas.includes(campo) ?? false;
  const ignoradas = columnas ? columnas.ignoradas.map((i) => `«${columnas.cabeceras[i]}»`) : [];
  const lista = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} y ${xs[xs.length - 1]}`);
  const grupoVa = revision.loQueEntra.porGrupo;

  return (
    <div className="stack gasto-excel">
      <div className="ing-contadores">
        <article className="stat ing-contador ing-contador--bien">
          <p className="stat__value">{formatNumber(listos)}</p>
          <p className="stat__label">{listos === 1 ? 'listo' : 'listos'}</p>
        </article>
        <article className={`stat ing-contador ing-contador--${revisar > 0 ? 'atencion' : 'neutro'}`}>
          <p className="stat__value">{formatNumber(revisar)}</p>
          <p className="stat__label">por revisar</p>
        </article>
        <article className="stat ing-contador ing-contador--neutro">
          <p className="stat__value">{formatMoney(revision.totalNet)}</p>
          <p className="stat__label">sin IVA{meses.size === 1 ? `, en ${formatMonthLong([...meses][0]).split(' ')[0]}` : ''}</p>
        </article>
      </div>

      {columnas && (
        <details className="gasto-excel__columnas">
          <summary>
            <span className="ing-ficha__chevron" aria-hidden="true">›</span> <strong>Así entendí tus columnas</strong> · {usadas} de {conNombre}{' '}
            <span className="gasto-excel__cambiar">Ver o cambiar</span>
          </summary>
          <ul className="gasto-excel__mapa">
            {columnas.cabeceras.map((h, i) => (
              <li key={i}>
                <SmartSelect
                  ariaLabel={`Qué es la columna «${h || i + 1}»`}
                  value={columnas.columnas[i] ?? 'ignorar'}
                  onChange={(v) => columnas.onCambiar(i, v as DestinoColumna)}
                  options={[...CAMPOS_GASTO, 'ignorar' as const].map((c) => ({ value: c, label: CAMPO_GASTO_LABELS[c] }))}
                />
                <span>← «{h || `Columna ${i + 1}`}»</span>
              </li>
            ))}
          </ul>
          {(ignoradas.length > 0 || !usa('categoria')) && (
            <p className="field__hint">
              {ignoradas.length > 0 && `${lista(ignoradas)} no ${ignoradas.length === 1 ? 'la uso' : 'las uso'}. `}
              {!usa('categoria') && 'La categoría la saco de cada proveedor.'}
            </p>
          )}
        </details>
      )}

      {fechas && (
        <p className="gasto-excel__fechas">
          Leí las fechas como {fechas.diaPrimero ? 'día/mes (03/04 = 3 de abril)' : 'mes/día (03/04 = 4 de marzo)'}.{' '}
          <button type="button" className="btn btn--link btn--sm" onClick={fechas.onCambiar}>Al revés</button>
        </p>
      )}

      {revisar > 0 && (
        <section className="ing-revisar" aria-labelledby="gastos-revisar">
          <h3 id="gastos-revisar">Revisa {revisar === 1 ? 'este' : `estos ${revisar}`}</h3>
          <p className="ing-revisar__sub">Arréglalos aquí mismo, sin volver al Excel.</p>
          {revision.porRevisar.slice(0, 40).map((item) => (
            <article key={item.row} className="ing-revisar__fila">
              <div className="ing-revisar__cabeza">
                <strong>{item.supplierName ?? item.input.concept} · {formatMoney(item.total)}</strong>
                <span>fila {item.row}</span>
              </div>
              {item.dudas.map((d, i) => (
                <div key={i} className="gasto-excel__duda">
                  <p>{d.mensaje}</p>
                  {arreglo(item, d)}
                </div>
              ))}
            </article>
          ))}
          {revisar > 40 && <p className="field__hint">Y {revisar - 40} más.</p>}
        </section>
      )}

      {revision.noSeGuardan.length > 0 && (
        <div className="ing-excel__aviso">
          <strong>{revision.noSeGuardan.length === 1 ? '1 fila no se puede guardar' : `${revision.noSeGuardan.length} filas no se pueden guardar`}</strong>
          <p>Arréglalas en el Excel y vuelve a subirlo: lo que ya esté en Alexia no se repite.</p>
          <ul>
            {revision.noSeGuardan.slice(0, 20).map((p) => (
              <li key={p.row}>Fila {p.row} · {p.name}: {p.message}</li>
            ))}
          </ul>
        </div>
      )}

      {(revision.descartados.length > 0 || revision.aInversiones.length > 0) && (
        <p className="field__hint">
          {revision.descartados.length > 0 && `No se guardan ${revision.descartados.length === 1 ? '1 fila' : `${revision.descartados.length} filas`} (repetidas, en 0 o devoluciones). `}
          {revision.aInversiones.length > 0 && `${revision.aInversiones.length === 1 ? '1 va' : `${revision.aInversiones.length} van`} a Inversiones.`}
        </p>
      )}

      {listos + revisar > 0 && (
        <section className="gasto-excel__entra" aria-labelledby="gastos-entra">
          <h3 id="gastos-entra">Lo que va a entrar</h3>
          <dl>
            {(['materia_prima', 'personal', 'generales'] as const).map((g) => (
              <div key={g}>
                <dt>{GRUPO[g]}</dt>
                <dd>{formatMoney(grupoVa[g])}</dd>
              </div>
            ))}
            {grupoVa.financieros > 0 && (
              <div>
                <dt>Financieros</dt>
                <dd>{formatMoney(grupoVa.financieros)}</dd>
              </div>
            )}
          </dl>
          <p className="field__hint">
            {[
              revision.loQueEntra.proveedoresNuevos > 0
                ? `Se ${revision.loQueEntra.proveedoresNuevos === 1 ? 'crea 1 proveedor nuevo' : `crean ${revision.loQueEntra.proveedoresNuevos} proveedores nuevos`}.`
                : '',
              revision.loQueEntra.porPagar > 0
                ? `${revision.loQueEntra.porPagar === 1 ? '1 gasto queda' : `${revision.loQueEntra.porPagar} gastos quedan`} «por pagar»${revision.loQueEntra.venceMasProximo ? ` (el más próximo vence el ${formatDate(revision.loQueEntra.venceMasProximo)})` : ''}.`
                : '',
              revision.loQueEntra.notas > 0 ? `${revision.loQueEntra.notas === 1 ? '1 es una nota' : `${revision.loQueEntra.notas} son notas`} sin factura.` : '',
            ].filter(Boolean).join(' ')}
          </p>
        </section>
      )}

      {listos > 0 && (
        <details className="ing-bien">
          <summary><span className="ing-ficha__chevron" aria-hidden="true">›</span> Ver {listos === 1 ? 'el que está bien' : `los ${listos} que están bien`}</summary>
          <ul className="gasto-excel__bien">
            {revision.listos.slice(0, 60).map((item) => (
              <li key={item.row}>
                {formatDate(item.input.date)} · <strong>{item.supplierName ?? item.input.concept}</strong> · {item.categoria?.label.split(' › ').slice(1).join(' › ') ?? ''} ·{' '}
                {formatMoney(item.input.netAmount)}
                {item.esNota ? ' · nota' : ''}
                {item.categoria?.deducida ? <span className="muted"> · la categoría la puso Alexia</span> : null}
              </li>
            ))}
          </ul>
          {listos > 60 && <p className="field__hint">… y {listos - 60} más.</p>}
        </details>
      )}
    </div>
  );
}
